import {type FormEvent, type ReactNode, useState} from "react";

import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {Loader2Icon, MoreHorizontalIcon, PlusIcon, ShieldOffIcon} from "lucide-react";
import {toast} from "sonner";

import {
  type ApiKeyView,
  type IssueKeyOutputBody,
  adminPlatformApiKeysIssue,
  adminPlatformApiKeysList,
  adminPlatformApiKeysRevoke,
} from "@/sdk";

import {Badge} from "@/components/ui/badge";
import {Button} from "@/components/ui/button";
import {Card, CardContent} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {Input} from "@/components/ui/input";
import {Label} from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {formatRelative} from "@/lib/format";

import {PlaintextKeyDialog} from "@/pages/admin/admin-org-api-keys";

const QK = ["admin", "platform-api-keys"] as const;

export const AdminPlatformApiKeys = () => {
  const qc = useQueryClient();

  const [issueOpen, setIssueOpen] = useState(false);
  const [issueName, setIssueName] = useState("");
  const [issuedKey, setIssuedKey] = useState<IssueKeyOutputBody | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ApiKeyView | null>(null);

  const query = useQuery({
    queryKey: QK,
    queryFn: async () => {
      const {data, error} = await adminPlatformApiKeysList();
      if (error) throw error;
      return data?.items ?? [];
    },
  });

  const invalidate = () => qc.invalidateQueries({queryKey: QK});

  const issueM = useMutation({
    mutationFn: () =>
      adminPlatformApiKeysIssue({
        body: {name: issueName.trim()},
      }),
    onSuccess: ({data, error}) => {
      if (error || !data) {
        toast.error(extractError(error));
        return;
      }
      setIssuedKey(data);
      setIssueOpen(false);
      setIssueName("");
      invalidate();
    },
  });

  const revokeM = useMutation({
    mutationFn: (keyId: string) =>
      adminPlatformApiKeysRevoke({path: {key_id: keyId}}),
    onSuccess: ({error}) => {
      if (error) {
        toast.error(extractError(error));
        return;
      }
      toast.success("API key revoked");
      setRevokeTarget(null);
      invalidate();
    },
  });

  const handleIssueSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!issueName.trim()) return;
    issueM.mutate();
  };

  const keys = query.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-semibold">Platform API keys</h1>
          <p className="text-sm text-muted-foreground">
            Tenant-less keys used by platform integrations — most importantly the Blesta
            module. These keys can act across all organizations.
          </p>
        </div>
        <Button onClick={() => setIssueOpen(true)}>
          <PlusIcon /> Issue new key
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {query.isLoading ? (
            <div className="flex items-center justify-center p-12 text-muted-foreground">
              <Loader2Icon className="size-5 animate-spin" />
            </div>
          ) : query.isError ? (
            <div className="p-6 text-sm text-destructive">Failed to load API keys.</div>
          ) : keys.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">
              No platform API keys issued yet.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Prefix</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Last used</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {keys.map((k) => {
                  const revoked = !!k.revoked_at;
                  return (
                    <TableRow key={k.id}>
                      <TableCell className="font-medium">{k.name}</TableCell>
                      <TableCell className="font-mono text-xs">{k.prefix}…</TableCell>
                      <TableCell>
                        {revoked ? (
                          <Badge variant="muted">revoked</Badge>
                        ) : (
                          <Badge variant="success">active</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {formatRelative(k.created_at)}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {formatRelative(k.last_used_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon-sm">
                              <MoreHorizontalIcon />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              variant="destructive"
                              disabled={revoked}
                              onSelect={() => setRevokeTarget(k)}
                            >
                              <ShieldOffIcon /> Revoke
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={issueOpen} onOpenChange={(o) => !o && setIssueOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Issue platform API key</DialogTitle>
            <DialogDescription>
              Give the key a human-readable name (e.g. "Blesta production"). The plaintext
              token is shown ONCE — capture it immediately.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleIssueSubmit} className="space-y-4">
            <Field id="key_name" label="Name">
              <Input
                id="key_name"
                required
                placeholder="Blesta production"
                value={issueName}
                onChange={(e) => setIssueName(e.target.value)}
              />
            </Field>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIssueOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={issueM.isPending || !issueName.trim()}>
                {issueM.isPending ? "Issuing…" : "Issue key"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <PlaintextKeyDialog issued={issuedKey} onClose={() => setIssuedKey(null)} />

      <Dialog open={revokeTarget !== null} onOpenChange={(o) => !o && setRevokeTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke platform API key?</DialogTitle>
            <DialogDescription>
              Revokes <span className="font-mono">{revokeTarget?.name}</span>. Any callers
              still using this key (including Blesta) will start receiving 401s immediately.
              This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={revokeM.isPending}
              onClick={() => revokeTarget && revokeM.mutate(revokeTarget.id)}
            >
              {revokeM.isPending ? "Revoking…" : "Revoke"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

function Field({id, label, children}: {id: string; label: string; children: ReactNode}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  );
}

function extractError(err: unknown): string {
  return (err as {detail?: string} | undefined)?.detail ?? "Request failed";
}
